import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useId, type ReactNode } from 'react';
import { Chip } from '../../components/Chip';
import { Icon } from '../../components/Icon';
import type { ImportDecision } from '../../lib/types';
import StammIcon from '../stammdaten/StammIcon';
import { PlanDecision } from './PlanDecision';
import type { BomGroup } from './planPresentation';

interface BomReviewProps {
  groups: readonly BomGroup[];
  decisions: Readonly<Record<string, ImportDecision>>;
  showUnchanged: boolean;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

function BomCard({ group, decisions, showUnchanged, disabled, onDecide }: Omit<BomReviewProps, 'groups'> & { group: BomGroup }): ReactNode {
  const { t } = useLingui();
  const headingId = useId();
  const visible = group.components.filter(({ entry }) => showUnchanged || entry.action !== 'unchanged');
  if (visible.length === 0) return null;
  const hiddenCount = group.components.length - visible.length;
  const { productName, productVersion } = group;
  const productLabel = productVersion ? t`${productName} · Version ${productVersion}` : productName;
  const componentCount = group.components.length;

  return (
    <article className="imp-bom" aria-labelledby={headingId}>
      <header className="imp-bom-head">
        <span className="imp-bom-icon"><StammIcon name="box" /></span>
        <div className="imp-bom-product">
          <div className="imp-bom-title">
            <h3 id={headingId}>{group.productName}</h3>
            {productVersion ? <Chip className="imp-version"><Trans>Version {productVersion}</Trans></Chip> : null}
          </div>
        </div>
        <span className="imp-bom-count">{t`${plural(componentCount, { one: '# Komponente', other: '# Komponenten' })}`}</span>
      </header>
      <table className="imp-bom-table" role="table">
        <caption className="visually-hidden"><Trans>Importvorschau: Stückliste für {productLabel}</Trans></caption>
        <thead role="rowgroup">
          <tr role="row">
            <th scope="col" role="columnheader"><Trans>Komponente</Trans></th>
            <th scope="col" role="columnheader"><Trans>Version</Trans></th>
            <th scope="col" role="columnheader"><Trans>Änderung</Trans></th>
            <th scope="col" role="columnheader" className="imp-bom-choice"><Trans>Import</Trans></th>
          </tr>
        </thead>
        <tbody role="rowgroup">
          {visible.map(({ entry, componentName, componentVersion, changes }) => {
            const decision = decisions[entry.entry_id] ?? null;
            const removal = entry.action === 'remove';
            const skipped = decision === 'existing' && !removal;
            const replacing = (entry.display?.replaces?.length ?? 0) > 0;
            const status = removal
              ? decision === 'incoming' ? t`Wird entfernt` : t`Fehlt im Scan · bleibt`
              : entry.display?.replaced_by_scan ? t`Durch neuere Version ersetzt`
              : skipped ? t`Übersprungen`
              : entry.action === 'create' ? (replacing ? t`Andere Version im Scan` : t`Neu in Stückliste`)
              : entry.action === 'update' ? t`Geändert` : t`Unverändert`;
            const component = componentVersion ? `${componentName} ${componentVersion}` : componentName;
            return (
              <tr key={entry.entry_id} role="row" className={skipped ? 'imp-bom-skipped' : undefined}>
                <th scope="row" role="rowheader" className="imp-bom-component">
                  <StammIcon name="component" />
                  <span>{componentName}</span>
                  {changes.length > 0 && !removal ? (
                    <dl className="imp-bom-changes">
                      {changes.map((change, index) => (
                        <div key={`${change.field}-${index}`}>
                          <dt>{change.field}</dt>
                          <dd>
                            {change.old !== null ? <><span className="visually-hidden"><Trans>Bisher:</Trans>{' '}</span><del>{change.old}</del> <Icon name="arrow-right" size={12} /> <span className="visually-hidden"><Trans>Neu:</Trans>{' '}</span></> : null}
                            {change.new}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </th>
                <td role="cell" className="imp-bom-version">
                  <span className="imp-mobile-label" aria-hidden="true"><Trans>Version</Trans></span>
                  <span>{componentVersion || t`Nicht angegeben`}</span>
                </td>
                <td role="cell" className="imp-bom-status">
                  <Chip tone={removal ? (decision === 'incoming' ? 'alert' : 'warn') : skipped || entry.action === 'unchanged' ? 'neutral' : entry.action === 'create' ? 'good' : 'warn'}>{status}</Chip>
                </td>
                <td role="cell" className="imp-bom-choice">
                  <PlanDecision
                    entry={entry}
                    label={t`${component} in ${productLabel}`}
                    decision={decision}
                    disabled={disabled}
                    onDecide={onDecide}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {hiddenCount > 0 ? (
        <p className="imp-bom-note">{t`${plural(hiddenCount, { one: '# unveränderte Komponente ausgeblendet.', other: '# unveränderte Komponenten ausgeblendet.' })}`}</p>
      ) : null}
    </article>
  );
}

export function BomReview({ groups, ...props }: BomReviewProps): ReactNode {
  return groups.map((group) => <BomCard key={group.key} group={group} {...props} />);
}
